'use client';

import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';

export interface PageSkeletonProps {
  /** Number of rows to display in the skeleton table */
  rows?: number;
  /** Whether to show action buttons in the header */
  showActions?: boolean;
}

/**
 * Loading skeleton for list pages with DataTable
 * Displays a header skeleton and table rows skeleton
 */
export function PageSkeleton({ rows = 5, showActions = true }: PageSkeletonProps) {
  return (
    <Box>
      {/* Header skeleton */}
      <Box sx={{ mb: 3 }}>
        <Skeleton variant="text" width={200} height={20} sx={{ mb: 1 }} />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <Box>
            <Skeleton variant="text" width={250} height={40} />
            <Skeleton variant="text" width={300} height={24} />
          </Box>
          {showActions && <Skeleton variant="rectangular" width={160} height={40} sx={{ borderRadius: 1 }} />}
        </Box>
      </Box>

      {/* Table skeleton */}
      <Box
        sx={{
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        {/* Table header */}
        <Box
          sx={{
            display: 'flex',
            gap: 2,
            p: 2,
            backgroundColor: 'action.hover',
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Skeleton variant="text" width="20%" height={24} />
          <Skeleton variant="text" width="25%" height={24} />
          <Skeleton variant="text" width="10%" height={24} />
          <Skeleton variant="text" width="15%" height={24} />
          <Skeleton variant="text" width="10%" height={24} />
          <Skeleton variant="text" width="15%" height={24} />
        </Box>

        {/* Table rows */}
        {Array.from({ length: rows }).map((_, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              gap: 2,
              p: 2,
              borderBottom: index < rows - 1 ? '1px solid' : 'none',
              borderColor: 'divider',
            }}
          >
            <Skeleton variant="text" width="20%" height={24} />
            <Skeleton variant="text" width="25%" height={24} />
            <Skeleton variant="text" width="10%" height={24} />
            <Skeleton variant="text" width="15%" height={24} />
            <Skeleton variant="rectangular" width="10%" height={24} sx={{ borderRadius: 2 }} />
            <Skeleton variant="text" width="15%" height={24} />
          </Box>
        ))}
      </Box>

      {/* Pagination skeleton */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, gap: 2 }}>
        <Skeleton variant="text" width={100} height={32} />
        <Skeleton variant="rectangular" width={200} height={32} sx={{ borderRadius: 1 }} />
      </Box>
    </Box>
  );
}
